<template>
  <Modal
    ref="createNewSubAccountModalRef"
    @onNegativeActionClick="closeNewSubAccountModal"
    @onPositiveActionClick="createNewSubAccount"
    :isPositiveActionPending="isCreateLoading"
    :isPositiveActionButtonDisabled="isCreateLoading"
    negativeActionText="Vazgeç"
    positiveActionText="Hesabı Ekle"
    :showIcon="false"
    :modalStyle="{ width: '460px' }"
  >
    <template v-slot:body>
      <div class="modal-body top">
        <h3 class="modal-title">Yeni Alt Hesap Ekle</h3>
        <div>
          <p class="modal-text">Alt Hesabın Kullanıcı Adı</p>
          <p class="subTitle">
            Eklemek istediğiniz alt hesap için bir kullanıcı adı belirleyin.
          </p>
        </div>

        <Input
          type="text"
          id="username"
          label="Kullanıcı Adı"
          size="lg"
          v-model:value="username"
          v-model:isError="isUsernameError"
          :helperText="usernameErrorText"
        />
      </div>

      <div class="modal-body buttom">
        <div>
          <p class="modal-text">Alt Hesabın Telefon Numarası</p>
          <p class="subTitle">
            Alt hesabı kullanacak kişiye ait telefon numarasını yazın.
          </p>
        </div>

        <div class="phone-info">
          <Input
            type="phone"
            id="code"
            label="Ülke Kodu"
            size="lg"
            value="TR (+90)"
            disabled
          />
          <Input
            type="phone"
            id="phone"
            label="Telefon Numarası"
            size="lg"
            v-model:value="phoneNumber"
            v-model:isError="isPhoneError"
            :isOnlyNumber="true"
            :helperText="phoneErrorText"
            maxlength="10"
          />
        </div>
      </div>
      <div class="info-box-wrapper">
        <div class="info-box">
          <img
            width="20px"
            height="20px"
            src="@/assets/images/icons/info-circle.svg"
          />
          <div class="info-box-text">
            <p>Yukarıdaki telefon numarasına SMS ile bir şifre göndereceğiz.</p>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { Modal, Input } from "@n11/so-ui-components";
import { ToastMessageTypes } from "@/utils/enum/toastMessageTypes";

const emit = defineEmits(["createNewSubAccount", "successModal"]);
const store = useStore();

const phoneNumber = ref("");
const username = ref("");
const isUsernameError = ref(false);
const isPhoneError = ref(false);
const usernameErrorText = ref("");
const phoneErrorText = ref("");

const createNewSubAccountModalRef = ref();
const isCreateLoading = ref(false);

const resetModalState = () => {
  isUsernameError.value = false;
  isPhoneError.value = false;
  usernameErrorText.value = "";
  phoneErrorText.value = "";
};

const openCreateNewSubAccountModal = () => {
  resetModalState();
  createNewSubAccountModalRef?.value?.openModal();
};

const closeNewSubAccountModal = () => {
  resetModalState();
  createNewSubAccountModalRef?.value?.hiddenModal();
};

const createNewSubAccount = async () => {
  try {
    validateUsername(username.value);
    if (isUsernameError.value) return;

    validatePhone(phoneNumber.value);
    if (isPhoneError.value) return;

    isCreateLoading.value = true;

    await store.dispatch("accounts/createSubAccount", {
      username: username.value,
      phoneNumber: phoneNumber.value,
    });

    emit("successModal");
    emit("createNewSubAccount");
    closeNewSubAccountModal();

    username.value = "";
    phoneNumber.value = "";
  } catch (error) {
    store.commit("common/SET_TOASTER_DATA", {
      type: ToastMessageTypes.ERROR,
      title: ToastMessageTypes.ERROR_TITLE,
      description: error.message || "Bir hata oluştu",
    });
  } finally {
    isCreateLoading.value = false;
  }
};

const validateUsername = (value) => {
  const validations = [
    {
      condition: !value.trim(),
      message: "Kullanıcı adı boş olamaz.",
    },
    {
      condition: value.length < 6 || value.length > 20,
      message: "Kullanıcı adı 6-20 karakter arasında olmalıdır.",
    },
    {
      condition: /^\d+$/.test(value),
      message: "Kullanıcı adı sadece rakamlardan oluşamaz.",
    },
    {
      condition: !/^([a-zA-Z])[a-zA-Z0-9]+$/.test(value),
      message: "Kullanıcı adı harfle başlamalı ve özel karakter içermemelidir.",
    },
  ];

  for (let validation of validations) {
    if (validation.condition) {
      usernameErrorText.value = validation.message;
      isUsernameError.value = true;
      return;
    }
  }
  isUsernameError.value = false;
  usernameErrorText.value = "";
};

const validatePhone = (value) => {
  if (!value.trim() || !/^\d+$/.test(value) || value.length !== 10) {
    phoneErrorText.value = "Geçerli bir telefon numarası girin.";
    isPhoneError.value = true;
    return;
  }

  isPhoneError.value = false;
  phoneErrorText.value = "";
};

watch([username, phoneNumber], ([newUsername, newPhone]) => {
  validateUsername(newUsername);
  validatePhone(newPhone);
});

defineExpose({
  openCreateNewSubAccountModal,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/utilities.scss";

.modal-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  &.top {
    margin-bottom: 14px;
  }
  &.buttom {
    margin-bottom: 4px;
  }
  .modal-title {
    text-align: start;
    position: absolute;
    top: 14px;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.2px;
  }
  .modal-text {
    text-align: start;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: -0.05px;
  }
  .subTitle {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  }
}
.phone-info {
  display: flex;
  width: 100%;
  gap: 8px;

  .input-wrapper:first-child {
    flex: 0 0 28%;
  }
}

.info-box-wrapper {
  .info-box {
    display: flex;
    background-color: $colorB10;
    padding: 16px;
    gap: 12px;
    border-radius: 6px;
    align-items: center;
    .info-box-text {
      color: $colorN80;
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: -0.05px;
      text-align: left;
    }
  }
}

:deep(.input-wrapper.error .input-helperText) {
  color: $colorE50;
  text-align: start;
}
</style>
